// Client-side CSV download for the Backend screens' Export buttons.
// Exports exactly what the operator is looking at (the filtered table rows) —
// callers map their row model to a flat { "Column label": value } object and
// this handles quoting + the download plumbing.

import { todayNyInput } from '@/lib/datetime';

const FORMULA_PREFIXES = new Set(['=', '+', '-', '@']);

export function escapeCsvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const raw = String(v);
  // Spreadsheet applications can execute formula-looking CSV strings. Preserve
  // actual numbers (including negative numbers), but force string values whose
  // first visible character is a formula marker to be interpreted as text.
  // Leading whitespace is significant data, so retain it after the guard.
  const firstVisible = raw.trimStart()[0] ?? '';
  const s = typeof v === 'string' && FORMULA_PREFIXES.has(firstVisible) ? `'${raw}` : raw;
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build a CSV from uniform records and trigger a browser download.
 * Column order comes from the first row's key order. No-op on empty input.
 */
export function downloadCsv(filename: string, rows: Record<string, unknown>[]): void {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]!);
  const lines = [
    headers.map(escapeCsvCell).join(','),
    ...rows.map((r) => headers.map((h) => escapeCsvCell(r[h])).join(',')),
  ];
  // BOM so Excel opens UTF-8 correctly.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  triggerDownload(filename, blob);
}

export function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** `pipeline-2026-07-03.csv`-style names, dated to the NY (ET) calendar day. */
export function csvFilename(prefix: string): string {
  return `${prefix}-${todayNyInput()}.csv`;
}
